import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import saveAs from "file-saver";
import { FC, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useDispatch } from "react-redux";
import { FormController, MuiModal } from "../../../../components";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { useToast } from "../../../../hooks/useToast";
import { toggleLoader } from "../../../../redux/slices/appState/appStateSlice";

import { useGetDownloadSmartTemplateByClientId } from "../../../../api/services/bulkCreateProgram/bulkCreateProgramApis";
import { ReactComponent as WarningIcon } from "../../../../assets/images/warning-icon.svg";
import { InformationBadge } from "../../../../common/informationBadge/informationBadge";
import { ClientAutoComplete } from "../../../../components/clientAutoComplete/ClientAutoComplete";
import { DATE_FORMAT } from "../../../../helpers/constants";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import {
  BulkCreateProgramDownloadFormKey,
  bulkImportBaseFileName,
} from "../../modelsAndSchema/Models";
import { BulkCreateProgramDownloadTemplateSchema } from "../../modelsAndSchema/ValidationSchemas";
import { programDownloadTemplateKeys } from "../../siteCore/siteCoreKeys";
import { BulkProgramDownloadTemplateWrapper } from "./BulkProgramDownloadSmartTemplate.style";

interface BulkProgramDownloadSmartTemplateProps {
  pageId: string;
  open: boolean;
  onClose: () => void;
}

export const BulkProgramDownloadSmartTemplate: FC<
  BulkProgramDownloadSmartTemplateProps
> = ({ pageId, open, onClose }) => {
  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const {
    fetchLabel,
    commonLabels,
    commonValidationMessages,
    fetchComponentTitle,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const {
    data: smartTemplateData,
    isPending: smartTemplatePending,
    isError: smartTemplateError,
    isSuccess: smartTemplateSuccess,
    mutateAsync: refetchSmartTemplateByClientId,
  } = useGetDownloadSmartTemplateByClientId();

  const [showWarning, setShowWarning] = useState(false);

  const getTemplateLabel = (key: string) =>
    fetchLabel(programDownloadTemplateKeys.downloadSmartTemplatePopupKey, key);

  useEffect(() => {
    smartTemplateError && setShowWarning(true);

    dispatch(toggleLoader({ isVisible: false }));
  }, [smartTemplateError]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: smartTemplatePending,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [smartTemplatePending]);

  useEffect(() => {
    if (smartTemplateSuccess) {
      onClose();
    }
  }, [smartTemplateSuccess]);

  const methods = useForm({
    mode: "all",
    defaultValues: {
      [BulkCreateProgramDownloadFormKey.clientId]: "",
    },
    resolver: yupResolver(
      BulkCreateProgramDownloadTemplateSchema(commonValidationMessages),
    ),
  });

  const { handleSubmit, control, getValues } = methods;

  const watchForm = useWatch({
    control,
    name: BulkCreateProgramDownloadFormKey.clientId,
  });

  useEffect(() => {
    if (watchForm) {
      setShowWarning(false);
    }
  }, [watchForm]);

  const getProgramSmartTemplate = () => {
    const { clientId } = getValues();
    refetchSmartTemplateByClientId({ clientId });
  };

  useEffect(() => {
    if (smartTemplateData) {
      if (smartTemplateData instanceof Blob) {
        saveAs(
          smartTemplateData,
          `${bulkImportBaseFileName}${dayjs().format(DATE_FORMAT.DD_MMM_YYYY_HHMMSS_UNDERSCORE)}.xlsx`,
        );
      } else {
        setShowWarning(true);
      }
    }
  }, [smartTemplateData]);

  return (
    <MuiModal
      onClose={onClose}
      title={fetchComponentTitle(
        programDownloadTemplateKeys.downloadSmartTemplatePopupKey,
      )}
      open={open}
      primaryButtonTitle={showWarning ? "" : commonLabels.download}
      secondaryButtonTitle={
        showWarning ? commonLabels.close : commonLabels.cancel
      }
      onSecondaryButtonClick={onClose}
      onPrimaryButtonClick={handleSubmit(getProgramSmartTemplate)}
    >
      <BulkProgramDownloadTemplateWrapper>
        <FormController
          name={BulkCreateProgramDownloadFormKey.clientId}
          control={control}
        >
          <ClientAutoComplete
            isMandatory
            label={getTemplateLabel(programDownloadTemplateKeys.selectClient)}
            formId={BulkCreateProgramDownloadFormKey.clientId}
            control={control}
          />
        </FormController>

        {showWarning && (
          <InformationBadge
            icon={<WarningIcon className="warning-icon" />}
            position="start"
            message={getTemplateLabel(
              programDownloadTemplateKeys.billingDetailsConfigurationAlertMessage,
            )}
            className="warning-badge"
          />
        )}
      </BulkProgramDownloadTemplateWrapper>
    </MuiModal>
  );
};
